import { PermissionCode } from '@gb-ticket/shared-types';
import { MonitorSmartphoneIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  useAttachTicketAsset,
  useAssets,
  useDetachTicketAsset,
} from '@/products/helpdesk/features/assets/use-assets';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import { ApiRequestError } from '@/lib/api';

export function TicketAssetsPanel({
  ticketId,
  ticketNumber,
}: {
  ticketId: string;
  ticketNumber: number;
}) {
  const me = useMe();
  const canRead = hasPermission(me.data, PermissionCode.ASSETS_READ);
  const canWrite = hasPermission(me.data, PermissionCode.ASSETS_WRITE);
  const linked = useAssets({
    page: 1,
    pageSize: 50,
    sortBy: 'name',
    sortOrder: 'asc',
    ticketId,
  });
  const [search, setSearch] = useState('');
  const term = search.trim();
  const suggestions = useAssets(
    {
      page: 1,
      pageSize: 8,
      sortBy: 'name',
      sortOrder: 'asc',
      q: term,
    },
    { enabled: term.length >= 2 },
  );
  const attach = useAttachTicketAsset();
  const detach = useDetachTicketAsset();

  const linkedIds = useMemo(
    () => new Set((linked.data?.items ?? []).map((item) => item.id)),
    [linked.data],
  );

  if (!canRead) {
    return null;
  }

  async function onLink(assetId: string) {
    try {
      await attach.mutateAsync({ assetId, body: { ticketId } });
      toast.success('Asset mit dem Ticket verknüpft.');
      setSearch('');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verknüpfung fehlgeschlagen.');
    }
  }

  async function onUnlink(assetId: string) {
    try {
      await detach.mutateAsync({ assetId, ticketId });
      toast.success('Verknüpfung entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Lösen fehlgeschlagen.');
    }
  }

  const hits = (suggestions.data?.items ?? []).filter((item) => !linkedIds.has(item.id));

  return (
    <TicketSectionCard
      tone="steel"
      icon={MonitorSmartphoneIcon}
      collapsible
      defaultOpen={false}
      title="Assets"
      description={`Geräte und Lizenzen zu Ticket #${ticketNumber}.`}
    >
      <div className="space-y-3">
        {(linked.data?.items ?? []).map((asset) => (
          <div key={asset.id} className="flex items-start justify-between gap-2">
            <Link
              to={`/helpdesk/assets/${asset.id}`}
              className="block text-sm text-primary hover:underline"
            >
              {asset.inventoryTag ? `${asset.inventoryTag} · ` : ''}
              {asset.name}
            </Link>
            {canWrite && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={detach.isPending}
                onClick={() => void onUnlink(asset.id)}
              >
                Lösen
              </Button>
            )}
          </div>
        ))}
        {linked.data?.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Kein Asset verknüpft.</p>
        )}
        {canWrite && (
          <div className="space-y-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Asset suchen …"
            />
            {search.trim().length >= 2 &&
              hits.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
                  disabled={attach.isPending}
                  onClick={() => void onLink(asset.id)}
                >
                  {asset.inventoryTag ? `${asset.inventoryTag} · ` : ''}
                  {asset.name}
                </button>
              ))}
          </div>
        )}
      </div>
    </TicketSectionCard>
  );
}
